/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useMemo, useState } from "react";

// react-router-dom components
import { useLocation, NavLink } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 2 React example components
import SidenavCollapse from "examples/Sidenav/SidenavCollapse";

// Custom styles for the Sidenav
import SidenavRoot from "examples/Sidenav/SidenavRoot";
import sidenavLogoLabel from "examples/Sidenav/styles/sidenav";

import { useAuth } from "auth/AuthContext";

// Material Dashboard 2 React context
import {
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
} from "context";

/** Nhóm đang thu gọn trên thanh bên, nhớ theo trình duyệt (lỗi / chặn lưu trữ thì dùng mặc định). */
const COLLAPSE_STORE = "petrealm_admin_sidenav_collapsed";

function readCollapsed(routes) {
  try {
    const saved = JSON.parse(localStorage.getItem(COLLAPSE_STORE));
    if (saved && typeof saved === "object") return saved;
  } catch (e) {
    // bỏ qua, dùng mặc định
  }
  return Object.fromEntries(
    routes.filter((r) => r.type === "title" && r.collapsedByDefault).map((r) => [r.key, true])
  );
}

function Sidenav({ color, brand, brandName, routes, ...rest }) {
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode, sidenavColor } = controller;
  const location = useLocation();
  const collapseName = location.pathname.split("/")[1];
  const { admin, logout } = useAuth();

  let textColor = "white";

  if (transparentSidenav || (whiteSidenav && !darkMode)) {
    textColor = "dark";
  } else if (whiteSidenav && darkMode) {
    textColor = "inherit";
  }

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  // Mục nào thuộc nhóm (tiêu đề) nào: mọi mục sau một tiêu đề tới tiêu đề kế tiếp.
  const groupOf = useMemo(() => {
    const map = {};
    let current = null;
    routes.forEach((r) => {
      if (r.type === "title") current = r.key;
      else if (r.key) map[r.key] = current;
    });
    return map;
  }, [routes]);
  const [collapsed, setCollapsed] = useState(() => readCollapsed(routes));
  const activeGroup = groupOf[collapseName];
  // Nhóm chứa trang đang mở luôn mở, kể cả khi người dùng đã thu gọn trước đó.
  const isClosed = (groupKey) =>
    Boolean(groupKey) && groupKey !== activeGroup && collapsed[groupKey];
  const toggleGroup = (groupKey) => {
    const next = { ...collapsed, [groupKey]: !collapsed[groupKey] };
    setCollapsed(next);
    try {
      localStorage.setItem(COLLAPSE_STORE, JSON.stringify(next));
    } catch (e) {
      // không lưu được thì chỉ nhớ trong phiên này
    }
  };

  useEffect(() => {
    // A function that sets the mini state of the sidenav.
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
      setTransparentSidenav(dispatch, window.innerWidth < 1200 ? false : transparentSidenav);
      setWhiteSidenav(dispatch, window.innerWidth < 1200 ? false : whiteSidenav);
    }

    /** 
     The event listener that's calling the handleMiniSidenav function when resizing the window.
    */
    window.addEventListener("resize", handleMiniSidenav);

    // Call the handleMiniSidenav function to set the state with the initial value.
    handleMiniSidenav();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, location]);

  // Render all the routes from the routes.js (All the visible items on the Sidenav)
  const renderRoutes = routes.map(({ type, name, icon, title, noCollapse, key, href, route }) => {
    let returnValue;

    // Thanh bên thu nhỏ chỉ còn biểu tượng: không ẩn mục nào, tiêu đề thành đường kẻ.
    if (type === "collapse" && !miniSidenav && isClosed(groupOf[key])) return null;

    if (type === "collapse") {
      returnValue = href ? (
        <Link
          href={href}
          key={key}
          target="_blank"
          rel="noreferrer"
          sx={{ textDecoration: "none" }}
        >
          <SidenavCollapse
            name={name}
            icon={icon}
            active={key === collapseName}
            noCollapse={noCollapse}
          />
        </Link>
      ) : (
        <NavLink key={key} to={route}>
          <SidenavCollapse name={name} icon={icon} active={key === collapseName} />
        </NavLink>
      );
    } else if (type === "title") {
      const closed = isClosed(key);
      const locked = key === activeGroup;
      returnValue = miniSidenav ? (
        <Divider key={key} light={!darkMode && !whiteSidenav && !transparentSidenav} />
      ) : (
        <MDBox
          key={key}
          component="button"
          type="button"
          onClick={() => !locked && toggleGroup(key)}
          aria-expanded={!closed}
          title={locked ? "Nhóm của trang đang mở" : closed ? "Bấm để mở nhóm" : "Bấm để thu gọn"}
          display="flex"
          alignItems="center"
          width="calc(100% - 16px)"
          pl={3}
          pr={2}
          mt={2}
          mb={0.5}
          mx={1}
          py={0.5}
          sx={{
            border: 0,
            background: "transparent",
            cursor: locked ? "default" : "pointer",
            borderRadius: 1,
            "&:hover": locked ? {} : { backgroundColor: "rgba(255, 255, 255, 0.06)" },
          }}
        >
          <MDTypography
            color={textColor}
            variant="caption"
            fontWeight="bold"
            textTransform="uppercase"
            flexGrow={1}
            textAlign="left"
            sx={{ opacity: 0.8 }}
          >
            {title}
          </MDTypography>
          {!locked && (
            <Icon
              fontSize="small"
              sx={{ color: textColor === "white" ? "#fff" : "inherit", opacity: 0.7 }}
            >
              {closed ? "expand_more" : "expand_less"}
            </Icon>
          )}
        </MDBox>
      );
    } else if (type === "divider") {
      returnValue = (
        <Divider
          key={key}
          light={
            (!darkMode && !whiteSidenav && !transparentSidenav) ||
            (darkMode && !transparentSidenav && whiteSidenav)
          }
        />
      );
    }

    return returnValue;
  });

  return (
    <SidenavRoot
      {...rest}
      variant="permanent"
      ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}
    >
      <MDBox pt={3} pb={1} px={4} textAlign="center">
        <MDBox
          display={{ xs: "block", xl: "none" }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{ cursor: "pointer" }}
        >
          <MDTypography variant="h6" color="secondary">
            <Icon sx={{ fontWeight: "bold" }}>close</Icon>
          </MDTypography>
        </MDBox>
        <MDBox component={NavLink} to="/" display="flex" alignItems="center">
          {brand && <MDBox component="img" src={brand} alt="Brand" width="2rem" />}
          <MDBox
            width={!brandName && "100%"}
            sx={(theme) => sidenavLogoLabel(theme, { miniSidenav })}
          >
            <MDTypography component="h6" variant="button" fontWeight="medium" color={textColor}>
              {brandName}
            </MDTypography>
          </MDBox>
        </MDBox>
      </MDBox>
      <Divider
        light={
          (!darkMode && !whiteSidenav && !transparentSidenav) ||
          (darkMode && !transparentSidenav && whiteSidenav)
        }
      />
      <List>{renderRoutes}</List>
      <MDBox p={2} mt="auto">
        {admin && !miniSidenav && (
          <MDTypography
            variant="caption"
            color={textColor}
            display="block"
            textAlign="center"
            mb={1}
            opacity={0.8}
          >
            {admin.name} ({admin.username})
          </MDTypography>
        )}
        <MDButton variant="gradient" color={sidenavColor} fullWidth onClick={logout}>
          <Icon>logout</Icon>
          {!miniSidenav && <>&nbsp;Đăng xuất</>}
        </MDButton>
      </MDBox>
    </SidenavRoot>
  );
}

// Setting default values for the props of Sidenav
Sidenav.defaultProps = {
  color: "info",
  brand: "",
};

// Typechecking props for the Sidenav
Sidenav.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
